import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  EyeSlash,
  ShieldCheck,
  ArrowRight,
  X,
  Warning,
} from "@phosphor-icons/react";
import {
  detectIncognito,
  rememberDismiss,
  wasDismissedThisSession,
} from "../lib/incognito";
import { useAuth } from "../lib/auth";

/**
 * Site-wide watchdog that runs the incognito detection once on mount.
 * Non-blocking — users can dismiss for the rest of the session. Stays
 * out of the way for signed-in users since they've already proven they
 * aren't farming the free trial.
 */
export default function IncognitoModal() {
  const { user, booted } = useAuth();
  const [shouldShow, setShouldShow] = useState(false);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!booted) return;
    // Signed-in users have already paid the registration toll — no nudge.
    if (user && user !== false) return;
    if (wasDismissedThisSession()) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await detectIncognito();
        if (cancelled) return;
        if (r.isPrivate) {
          setDetails(r);
          setShouldShow(true);
        }
      } catch (e) {
        // detection itself failed — fail open, no modal
        console.warn("incognito detection error", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [booted, user]);

  const dismiss = () => {
    rememberDismiss();
    setShouldShow(false);
  };

  if (!shouldShow) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="incognito-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="incog-title"
    >
      {/* Backdrop with blur for that "premium" gating feel */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={dismiss}
        aria-hidden="true"
      />
      <div className="relative bg-white border-2 border-black shadow-brutal max-w-lg w-full">
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 h-8 w-8 border border-[var(--border)] hover:bg-[var(--bg-2)] flex items-center justify-center"
          data-testid="incognito-dismiss"
          aria-label="Dismiss"
        >
          <X size={14} weight="bold" />
        </button>

        <div className="p-6 md:p-8">
          <div className="h-12 w-12 border-2 border-black flex items-center justify-center bg-[var(--accent)] text-white">
            <EyeSlash size={22} weight="fill" />
          </div>
          <div className="micro-label mt-5 flex items-center gap-2">
            <Warning size={12} weight="bold" /> Private browsing detected
          </div>
          <h2
            id="incog-title"
            className="font-display font-black text-2xl md:text-3xl tracking-tight leading-tight mt-2"
          >
            Use a regular window to keep your free credits.
          </h2>
          <p className="mt-4 text-sm text-[var(--fg-2)] leading-relaxed">
            Looks like you're in incognito / private browsing. Your{" "}
            <strong>20 free credits</strong> are tied to your account — sign up
            in a normal window so they don't disappear when you close this tab.
          </p>

          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <ShieldCheck
                size={16}
                weight="fill"
                className="text-[var(--success)] mt-0.5 shrink-0"
              />
              <span>
                <strong className="font-display">Your data stays yours.</strong>{" "}
                We never log IPs. Sign-up is the only way we tell new users
                apart from old ones.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <ShieldCheck
                size={16}
                weight="fill"
                className="text-[var(--success)] mt-0.5 shrink-0"
              />
              <span>
                <strong className="font-display">No card, no commitment.</strong>{" "}
                Free trial unlocks 4 full conversions plus Perfect Vector mode.
              </span>
            </li>
          </ul>

          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/register"
              onClick={dismiss}
              className="btn-accent justify-center"
              data-testid="incognito-register-cta"
            >
              Create free account <ArrowRight size={14} weight="bold" />
            </Link>
            <button
              onClick={dismiss}
              className="btn-ghost justify-center"
              data-testid="incognito-continue-anyway"
            >
              Continue browsing
            </button>
          </div>

          {details && (
            <div className="mt-5 pt-4 border-t border-[var(--border)] text-[10px] font-mono text-[var(--fg-2)] tracking-widest">
              DETECTION CONFIDENCE · {details.score}/4 SIGNALS
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
